import React, { useMemo } from 'react';
import { CustomInputProps } from '../../../types/summarizer';
import { useSummarizer } from '../../../core/store/useSummarizer';
import SuggestedVideos from '../../SuggestedVideos';
import { Input, ErrorAlert, parseErrorMessage, SummaryOptionsBar, ProcessingButton } from '@components/ui';
import { SafeImage } from '@components/ui/safe-image';
import { SOURCE_TYPES } from '../../../constants/summarizer';
import { pluginUrl } from '../../../lib/utils';

const YouTubeInputSection: React.FC<CustomInputProps> = ({ config, className = '' }) => {
  const {
    urlInput,
    urlError,
    summaryType,
    tone,
    setUrlInput,
    validateUrl,
    clearError,
    setSummaryType,
    setTone,
    summarize,
    isLoading: storeLoading,
  } = useSummarizer();

  // Get the plugin URL from WordPress global or use a fallback

  const handleSubmit = async () => {
    if (!urlInput.trim()) return;
    const isValid = validateUrl(urlInput, config.inputs.url?.validation);
    if (!isValid) return;

    try {
      await summarize({
        link_or_id: urlInput.trim(),
        source: SOURCE_TYPES.URL,
        summaryType,
        tone,
      });
    } catch (error) {
      // Error is handled by the store
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !storeLoading && urlInput.trim()) {
      handleSubmit();
    }
  };

  const handleSuggestedVideoClick = (videoUrl: string) => {
    setUrlInput(videoUrl);
    clearError();
  };

  const isValidUrl = () => {
    if (!urlInput.trim()) return false;
    try {
      new URL(urlInput);
      return validateUrl(urlInput, config.inputs.url?.validation);
    } catch {
      return false;
    }
  };

  const canSubmit = isValidUrl() && !urlError && !storeLoading;
  const loading = storeLoading;

  const suggestedVideos = useMemo(() => {
    return config?.inputs?.url?.examples || [];
  }, [config]);

  return (
    <div className={`youtube-input-section ${className}`}>
      {/* Input Section */}
      <div className="w-full space-y-10">
        {/* Error Display */}
        {urlError && (
          <ErrorAlert
            {...parseErrorMessage(urlError)}
            onDismiss={() => {
              clearError();
              if (urlError) setUrlInput('');
            }}
            className="mb-4 animate-in fade-in slide-in-from-top-2"
          />
        )}

        <div className="flex justify-between gap-5">
          {/* Main Input */}
          <div className="hidden w-[30%] lg:!flex items-center gap-5">
            <SafeImage
              src={`${pluginUrl}assets/images/youtube-summarizer-thumbnail.svg`}
              alt="YouTube Summarizer"
              className="w-[calc(100%-5rem)] h-full"
              fallback={
                <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center"></div>
              }
            />
            <SafeImage
              src={`${pluginUrl}assets/images/custom-arrow.svg`}
              alt="YouTube Summarizer"
              className="w-20 h-14"
              fallback={<div></div>}
            />
          </div>

          <div className="border w-full lg:!w-[calc(100%-40%)] p-3 rounded-lg">
            <Input
              type="url"
              id="youtube-input"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={config.inputs?.url?.placeholder ?? 'Past YouTube link here...'}
              disabled={loading}
              className="w-full h-28 px-6 text-lg border !border-sky-600 !rounded-xl !outline-none !focus:outline-none transition-colors"
            />

            {/* Dropdowns and Button Row */}
            <div className="flex flex-col sm:!flex-row items-stretch sm:!items-center mt-4 gap-3">
              {(config.inputs?.showSummaryOptions !== false) && (
                <SummaryOptionsBar
                  summaryType={summaryType}
                  tone={tone}
                  onSummaryTypeChange={setSummaryType}
                  onToneChange={setTone}
                  disabled={loading}
                  className="flex-1"
                  gap="md"
                  showSummaryType={config.inputs?.showSummaryType !== false}
                  showTone={config.inputs?.showTone !== false}
                />
              )}
              <ProcessingButton
                onClick={handleSubmit}
                disabled={!canSubmit}
                isLoading={loading}
                submitButtonText={config.inputs?.submitButtonText || 'Generate Summary'}
                className="sm:!ml-auto min-w-[180px]"
              />
            </div>
          </div>
        </div>

        {/* Suggested Videos Section */}
        {suggestedVideos && suggestedVideos.length > 0 && (
          <div className="border-t !my-4 pt-3">
            <SuggestedVideos videos={suggestedVideos} onVideoClick={handleSuggestedVideoClick} />
          </div>
        )}

        {/* Features Section - Conditional based on config */}
        {config.ui?.promoSection?.enabled !== false && (
          <div className="text-center mb-8">
            <div className="text-2xl font-semibold flex items-center justify-center gap-2 text-gray-900">
              <p>{config.ui?.promoSection?.title || 'Summarize YouTube videos in 30+ languages'}</p>
              {(config.ui?.promoSection?.image || `${pluginUrl}assets/images/flag.svg`) && (
                <SafeImage
                  src={config.ui?.promoSection?.image || `${pluginUrl}assets/images/flag.svg`}
                  alt=""
                  className="hidden md:!block w-28 h-8"
                  fallback={<div></div>}
                />
              )}
            </div>
            {config.ui?.promoSection?.content && (
              <div className="mt-4 text-gray-600">
                {config.ui?.promoSection?.content}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default YouTubeInputSection;
